import type { EstimateLine, EstimateLineConfidence, EstimateLineReviewStatus } from "@/types/mvpEstimate";

/** Prefiks `id` wiersza tabeli — spójny z `scrollToEstimateLine`. */
export const ESTIMATE_LINE_ANCHOR_PREFIX = "mvp-estimate-line-";

export function estimateLineElementId(lineId: string): string {
  return `${ESTIMATE_LINE_ANCHOR_PREFIX}${lineId}`;
}

function trimText(s: string | null | undefined): string {
  return (s ?? "").trim();
}

function effectiveConfidence(line: EstimateLine): EstimateLineConfidence {
  return line.confidence ?? "medium";
}

function effectiveReview(line: EstimateLine): EstimateLineReviewStatus {
  return line.reviewStatus ?? "draft";
}

/** Logiczny sourceType zgodny z normalizeEstimateLineTrust (stare zapisy). */
function effectiveSourceType(line: EstimateLine): EstimateLine["sourceType"] {
  return line.sourceType ?? (line.source === "generator" ? "heuristic" : "manual_edit");
}

/**
 * Czy pozycja ma widoczny „proof” w polach linii (założenia / uzasadnienie ilości / notatki).
 */
export function lineHasProofContent(line: EstimateLine): boolean {
  return Boolean(
    trimText(line.assumptionText) || trimText(line.quantityBasis) || trimText(line.notes)
  );
}

/**
 * Pozycje z heurystyki kreatora nie wymagają per-liniowego opisu — założenia są w sekcji kreatora.
 * Pozostałe (np. manual_edit) bez treści w proof liczą się jako brak uzasadnienia.
 */
function lineNeedsProofLine(line: EstimateLine): boolean {
  if (effectiveSourceType(line) === "heuristic") return false;
  return !lineHasProofContent(line);
}

export type EstimateReviewSummary = {
  lowConfidenceCount: number;
  needsReviewCount: number;
  missingProofCount: number;
  /** Gotowe tylko przy zerowych problemach w trzech licznikach. */
  readyToSend: boolean;
  /** id pozycji z dowolnym problemem (kolejność jak w kosztorysie). */
  problemLineIds: string[];
};

function lineHasIssue(line: EstimateLine): boolean {
  if (effectiveConfidence(line) === "low") return true;
  if (effectiveReview(line) === "needs_review") return true;
  if (lineNeedsProofLine(line)) return true;
  return false;
}

/**
 * Podsumowanie przed ofertą — lekkie liczniki + lista id do nawigacji.
 */
export function computeEstimateReviewSummary(lines: EstimateLine[]): EstimateReviewSummary {
  let lowConfidenceCount = 0;
  let needsReviewCount = 0;
  let missingProofCount = 0;
  const problemLineIds: string[] = [];

  for (const line of lines) {
    if (effectiveConfidence(line) === "low") lowConfidenceCount += 1;
    if (effectiveReview(line) === "needs_review") needsReviewCount += 1;
    if (lineNeedsProofLine(line)) missingProofCount += 1;
    if (lineHasIssue(line)) problemLineIds.push(line.id);
  }

  const readyToSend =
    lowConfidenceCount === 0 && needsReviewCount === 0 && missingProofCount === 0;

  return {
    lowConfidenceCount,
    needsReviewCount,
    missingProofCount,
    readyToSend,
    problemLineIds,
  };
}
